import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import apiService from '../../services/apiService';
import { RefreshCw, MessageSquare, User, Bot, AlertCircle, Play, Pause, Send, Clock } from "lucide-react";
import { toast } from "react-hot-toast";

// --- INTERNAL COMPONENTS (Robust, No External Deps) ---

const Card = ({ children, className = "" }) => (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${className}`}>
        {children}
    </div>
);

const Badge = ({ children, variant = 'neutral', className = "" }) => {
    const variants = {
        neutral: "bg-slate-100 text-slate-600 border-slate-200",
        success: "bg-emerald-50 text-emerald-700 border-emerald-200",
        warning: "bg-amber-50 text-amber-700 border-amber-200",
        error: "bg-red-50 text-red-700 border-red-200",
        info: "bg-blue-50 text-blue-700 border-blue-200",
        human: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${variants[variant] || variants.neutral} ${className}`}>
            {children}
        </span>
    );
};

const Button = ({ children, variant = "primary", size = "default", onClick, disabled, className = "" }) => {
    const base = "inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
    const variants = {
        primary: "bg-blue-600 text-white hover:bg-blue-700 shadow-sm",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200",
        outline: "border border-slate-200 bg-white hover:bg-slate-50 text-slate-700",
        ghost: "hover:bg-slate-100 text-slate-600",
        destructive: "bg-white border border-rose-200 text-rose-600 hover:bg-rose-50",
    };
    const sizes = {
        default: "h-9 px-4 py-2 text-sm",
        sm: "h-8 px-3 text-xs",
        icon: "h-9 w-9",
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant] || variants.primary} ${sizes[size] || sizes.default} ${className}`}>
            {children}
        </button>
    );
};

// --- MAIN COMPONENT ---

const LiveChatMonitor = () => {
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [operatorMessage, setOperatorMessage] = useState("");
    const scrollRef = useRef(null);

    // 1. Poll Active Chats
    const fetchChats = async () => {
        try {
            const data = await apiService.getActiveChats(1440); // Last 24h
            setChats(data || []);
        } catch (error) {
            console.error("Failed to fetch chats:", error);
        }
    };

    useEffect(() => {
        fetchChats();
        const interval = setInterval(fetchChats, 5000);
        return () => clearInterval(interval);
    }, []);

    // 2. Poll History for Selected Chat
    const fetchHistory = async (leadId) => {
        try {
            const history = await apiService.makeRequest(`/monitor/chat/${leadId}/history`);
            setChatHistory(history || []);
        } catch (error) {
            console.error("Failed to fetch history:", error);
        }
    };

    useEffect(() => {
        if (!selectedChat) return;
        fetchHistory(selectedChat.lead_id);
        const interval = setInterval(() => fetchHistory(selectedChat.lead_id), 3000);
        return () => clearInterval(interval);
    }, [selectedChat]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [chatHistory, selectedChat]);

    // 3. Actions
    const handleTakeover = async () => {
        if (!selectedChat) return;
        try {
            await apiService.takeoverChat(selectedChat.lead_id);
            toast.success("Human Mode Activated");
            setSelectedChat(prev => ({ ...prev, is_human_mode: true }));
            setChats(prev => prev.map(c => c.lead_id === selectedChat.lead_id ? { ...c, is_human_mode: true } : c));
        } catch (error) {
            toast.error("Failed to take over");
        }
    };

    const handleRelease = async () => {
        if (!selectedChat) return;
        try {
            await apiService.releaseChat(selectedChat.lead_id);
            toast.success("AI Mode Restored");
            setSelectedChat(prev => ({ ...prev, is_human_mode: false }));
            setChats(prev => prev.map(c => c.lead_id === selectedChat.lead_id ? { ...c, is_human_mode: false } : c));
        } catch (error) {
            toast.error("Failed to release chat");
        }
    };

    const handleSendMessage = async () => {
        if (!selectedChat || !operatorMessage.trim()) return;
        try {
            await apiService.sendOperatorMessage(selectedChat.lead_id, operatorMessage);
            setOperatorMessage("");
            fetchHistory(selectedChat.lead_id);
        } catch (error) {
            toast.error("Failed to send message");
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-[calc(100vh-12rem)] min-h-[600px]">
            {/* Active Chats Sidebar */}
            <div className="md:col-span-4 h-full flex flex-col">
                <Card className="flex flex-col h-full border-slate-200">
                    <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Active Chats</h3>
                            <Badge variant="neutral">{chats.length}</Badge>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {chats.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                                <MessageSquare className="w-8 h-8 opacity-20 mb-2" />
                                <span className="text-sm">No active chats</span>
                            </div>
                        )}
                        {chats.map(chat => (
                            <div
                                key={chat.lead_id}
                                onClick={() => setSelectedChat(chat)}
                                className={`
                                    group relative p-3 rounded-lg border cursor-pointer transition-all duration-200
                                    ${selectedChat?.lead_id === chat.lead_id
                                        ? 'bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-500/20'
                                        : 'bg-white border-slate-100 hover:border-slate-300 hover:shadow-sm'
                                    }
                                `}
                            >
                                <div className="flex justify-between items-start mb-1.5">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${chat.is_human_mode ? 'bg-rose-500' : 'bg-emerald-500 animate-pulse'}`} />
                                        <span className="font-semibold text-sm text-slate-800">{chat.full_name || chat.email || "Visitor"}</span>
                                    </div>
                                    <Badge variant={chat.is_human_mode ? 'human' : 'neutral'}>
                                        {chat.is_human_mode ? 'HUMAN' : 'AI Agent'}
                                    </Badge>
                                </div>
                                <div className="text-xs text-slate-500 truncate pl-4 mb-2">
                                    {chat.last_message || "No messages yet..."}
                                </div>
                                <div className="flex justify-between items-center pl-4 border-t border-slate-50 pt-2">
                                    <span className="text-[10px] text-slate-400 font-mono">ID: {chat.lead_id.toString().slice(0, 6)}...</span>
                                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                        <Clock size={10} />
                                        {new Date(chat.last_active).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Chat Details Area */}
            <div className="md:col-span-8 h-full flex flex-col">
                <Card className="h-full flex flex-col shadow-lg border-slate-200">
                    {selectedChat ? (
                        <>
                            {/* Header */}
                            <div className="py-4 px-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="h-10 w-10 flex items-center justify-center rounded-full bg-slate-200 text-slate-500 border border-slate-300">
                                        <User className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-slate-900">{selectedChat.full_name || "Visitor"}</h3>
                                        <p className="text-xs text-slate-500">{selectedChat.email || 'No email provided'} • {selectedChat.platform || 'Web Widget'}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    {selectedChat.is_human_mode ? (
                                        <Button variant="outline" size="sm" onClick={handleRelease} className="gap-2">
                                            <Bot className="w-4 h-4 text-emerald-600" />
                                            Resume AI
                                        </Button>
                                    ) : (
                                        <Button variant="destructive" size="sm" onClick={handleTakeover} className="gap-2">
                                            <Pause className="w-4 h-4" />
                                            Take Over
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Transcript */}
                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50" ref={scrollRef}>
                                <div className="space-y-4">
                                    {chatHistory.map((msg) => (
                                        <div key={msg.id} className={`flex w-full ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                                            <div className={`flex flex-col max-w-[85%] ${msg.role === 'user' ? 'items-start' : 'items-end'}`}>
                                                <div className="text-[10px] text-slate-400 mb-1 px-1 font-medium tracking-wide uppercase">
                                                    {msg.role === 'user' ? 'Visitor' : 'AI Assistant'}
                                                </div>
                                                <div
                                                    className={`
                                                        px-5 py-3 text-sm leading-relaxed shadow-sm rounded-2xl
                                                        ${msg.role === 'user'
                                                            ? 'bg-white border border-slate-200 text-slate-700 rounded-tl-none'
                                                            : 'bg-blue-600 text-white rounded-tr-none'
                                                        }
                                                    `}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                                                </div>
                                                <span className="text-[10px] text-slate-300 mt-1 px-1">
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Input */}
                            <div className="p-4 border-t border-slate-100 bg-white">
                                {selectedChat.is_human_mode ? (
                                    <div className="flex gap-3 relative">
                                        <textarea
                                            value={operatorMessage}
                                            onChange={(e) => setOperatorMessage(e.target.value)}
                                            placeholder="Type message..."
                                            className="flex-1 min-h-[50px] p-3 text-sm rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize-none transition-all"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                        />
                                        <Button
                                            onClick={handleSendMessage}
                                            size="icon"
                                            className="h-full aspect-square"
                                        >
                                            <Send className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-4 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                                        <div className="flex items-center gap-2 mb-1 text-slate-600">
                                            <Bot className="w-4 h-4" />
                                            <span className="font-medium text-sm">AI Active</span>
                                        </div>
                                        <p className="text-xs text-slate-400">Click "Take Over" to intervene.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-300 bg-slate-50/50">
                            <MessageSquare className="w-16 h-16 mb-4 opacity-20" />
                            <p className="text-lg font-medium text-slate-400">Select a chat to monitor</p>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default LiveChatMonitor;
